// CSR-2287/CSR-2250 — pure classification, ordering, and copy for the
// producer-facing "Policies on this Certificate" picker. No React so the
// rules can be unit tested without rendering, and so the component (which
// owns layout/a11y only) cannot drift from the spec's exact wording.
import {
  isCoiDefaultSelectedPolicy,
  isCoiSelectablePolicy,
  isDefaultCoiPolicySelection,
  COI_RENEWAL_OFFER_STATUS,
  type Policy,
} from "@shared/schema";
import {
  policyHasCoiCoverage,
  selectCoiCertificateCandidates,
} from "@shared/coi-coverage";
import {
  AOR_COI_POLICY_LOCKED_REASON,
  isAorFlagged,
} from "../policies/aor";

// CSR-2287 — the default rule is the server's own (`@shared/schema`), not a
// client twin: a drift here would mislabel which certificate the saved template
// describes. Re-exported so this module stays the picker's single door.
export { isDefaultCoiPolicySelection };

export type CoiPickerPolicyKind = "in_force" | "renewal_offer" | "locked";

export interface CoiPickerOptions {
  excludeAor?: boolean;
}

export interface CoiPickerPolicyClassification {
  kind: CoiPickerPolicyKind;
  lockedReason?: string;
}

// Per-status copy for a locked row's tooltip/aria-describedby text. Renewal
// Offer is handled separately below since its lock reason depends on the
// effective date, not the status alone.
const LOCKED_REASON_BY_STATUS: Record<string, string> = {
  expired: "Expired terms can’t be added to a certificate.",
  cancelled: "Cancelled terms can’t be added to a certificate.",
  renewed: "Renewed terms can’t be added to a certificate.",
  "non-renewal": "Non-renewed terms can’t be added to a certificate.",
  "flat cancel": "Flat cancelled terms can’t be added to a certificate.",
};
const RENEWAL_OFFER_LOCKED_REASON =
  "This renewal offer’s effective date has passed; it was never bound.";
const DEFAULT_LOCKED_REASON = "This term can’t be added to a certificate.";

function normalizedStatus(status: string | null | undefined): string {
  return (status ?? "").trim().toLowerCase();
}

// CSR-2333 — `isDefaultCoiPolicySelection` is the server's own rule and knows
// nothing of AOR, so the client drops AOR'd policies before asking it rather
// than changing the shared semantics.
export function withoutAorPolicies<T extends Pick<Policy, "aorIdentifiedAt">>(
  policies: T[],
  { excludeAor = false }: CoiPickerOptions = {},
): T[] {
  return excludeAor ? policies.filter((p) => !isAorFlagged(p)) : policies;
}

export function classifyCoiPickerPolicy(
  policy: Pick<Policy, "status" | "effectiveDate" | "aorIdentifiedAt">,
  today: Date = new Date(),
  { excludeAor = false }: CoiPickerOptions = {},
): CoiPickerPolicyClassification {
  if (excludeAor && isAorFlagged(policy)) {
    return { kind: "locked", lockedReason: AOR_COI_POLICY_LOCKED_REASON };
  }
  if (isCoiDefaultSelectedPolicy(policy)) return { kind: "in_force" };

  const status = normalizedStatus(policy.status);
  if (status === COI_RENEWAL_OFFER_STATUS) {
    if (isCoiSelectablePolicy(policy, today)) return { kind: "renewal_offer" };
    return { kind: "locked", lockedReason: RENEWAL_OFFER_LOCKED_REASON };
  }

  return {
    kind: "locked",
    lockedReason: LOCKED_REASON_BY_STATUS[status] ?? DEFAULT_LOCKED_REASON,
  };
}

export function defaultCoiPolicySelection(
  policies: Pick<Policy, "id" | "status" | "aorIdentifiedAt">[],
  options: CoiPickerOptions = {},
): string[] {
  return withoutAorPolicies(policies, options)
    .filter(isCoiDefaultSelectedPolicy)
    .map((policy) => policy.id);
}

const GROUP_ORDER: Record<CoiPickerPolicyKind, number> = {
  in_force: 0,
  renewal_offer: 1,
  locked: 2,
};

function compareByExpirationDesc(
  a: Pick<Policy, "id" | "expirationDate">,
  b: Pick<Policy, "id" | "expirationDate">,
): number {
  const cmp = (b.expirationDate ?? "").localeCompare(a.expirationDate ?? "");
  if (cmp !== 0) return cmp;
  return a.id.localeCompare(b.id);
}

export function orderCoiPickerPolicies<
  T extends Pick<
    Policy,
    "id" | "status" | "effectiveDate" | "expirationDate" | "aorIdentifiedAt"
  >,
>(
  policies: T[],
  today: Date = new Date(),
  options: CoiPickerOptions = {},
): T[] {
  return policies
    .map((policy) => ({
      policy,
      kind: classifyCoiPickerPolicy(policy, today, options).kind,
    }))
    .sort((a, b) => {
      const groupDiff = GROUP_ORDER[a.kind] - GROUP_ORDER[b.kind];
      return groupDiff !== 0
        ? groupDiff
        : compareByExpirationDesc(a.policy, b.policy);
    })
    .map((entry) => entry.policy);
}

export type CoiPolicySelectionSummaryTone = "default" | "custom" | "empty";

export interface CoiPolicySelectionSummary {
  selectedCount: number;
  isDefault: boolean;
  isEmpty: boolean;
  selectedMissingLimitsCount: number;
  summaryText: string;
  summaryTone: CoiPolicySelectionSummaryTone;
  headerWarningText: string | null;
  emptyErrorText: string | null;
}

export function summarizeCoiPolicySelection(
  allPolicies: Policy[],
  selectedIds: string[],
  options: CoiPickerOptions = {},
): CoiPolicySelectionSummary {
  const policies = withoutAorPolicies(allPolicies, options);
  const selectedCount = selectedIds.length;
  const isEmpty = selectedCount === 0;
  const isDefault = isDefaultCoiPolicySelection(policies, selectedIds);
  const selectedSet = new Set(selectedIds);
  const selectedMissingLimitsCount = policies.filter(
    (policy) => selectedSet.has(policy.id) && !policyHasCoiCoverage(policy),
  ).length;

  const summaryText = isEmpty
    ? "No policy selected."
    : `${selectedCount} ${selectedCount === 1 ? "policy" : "policies"} included${
        isDefault ? " (all in-force — default)" : ""
      }`;

  const summaryTone: CoiPolicySelectionSummaryTone = isEmpty
    ? "empty"
    : isDefault
      ? "default"
      : "custom";

  // Red (the empty error) wins over the orange coverage warning — no point
  // warning about a limit gap on a selection that is itself invalid.
  const headerWarningText =
    isEmpty || selectedMissingLimitsCount === 0
      ? null
      : `${selectedMissingLimitsCount} selected ${
          selectedMissingLimitsCount === 1 ? "policy has" : "policies have"
        } no coverage limits`;

  const emptyErrorText = isEmpty
    ? "Select at least one policy to include on the certificate."
    : null;

  return {
    selectedCount,
    isDefault,
    isEmpty,
    selectedMissingLimitsCount,
    summaryText,
    summaryTone,
    headerWarningText,
    emptyErrorText,
  };
}

// Shown on ANY term lacking coverage limits, checked or not — a blank-COI
// risk the picker should surface before the send, not after.
export function coiPolicyRowWarning(policy: Policy): string | null {
  return policyHasCoiCoverage(policy)
    ? null
    : "This policy has no coverage limits which causes a blank COI.";
}

export const COI_PICKER_HELPER_COPY =
  "By default every in-force policy is included. Cancelled, expired, and renewed terms can’t be selected.";
export const COI_PICKER_TITLE = "Policies on this Certificate";

// CSR-2287 — `GET /api/policies/:id/coi-template-data` answers the same
// `CoiTemplateData` shape whether a saved row exists or it just built a default,
// so the client cannot tell the two apart without a contract change. Worded
// conditionally for that reason.
export const COI_CUSTOM_SELECTION_TEMPLATE_NOTICE =
  "A custom policy selection is rendered live from the selected policies. If this insured has a saved COI template, it is not used for this certificate.";
export const COI_NO_SELECTABLE_POLICY_REASON =
  "No selectable policy is available to put on a certificate.";
export const COI_POLICY_NOT_SELECTABLE_MESSAGE =
  "A selected policy can no longer be added to a certificate. Review the policies on this certificate and try again.";
export const COI_POLICY_SET_MISMATCH_MESSAGE =
  "The certificate was issued with a different policy set than selected";

export interface CoiPickerPolicyKindCounts {
  inForce: number;
  renewalOffer: number;
  locked: number;
}

export function countCoiPickerPolicyKinds(
  policies: Pick<Policy, "status" | "effectiveDate" | "aorIdentifiedAt">[],
  today: Date = new Date(),
  options: CoiPickerOptions = {},
): CoiPickerPolicyKindCounts {
  const counts: CoiPickerPolicyKindCounts = {
    inForce: 0,
    renewalOffer: 0,
    locked: 0,
  };
  for (const policy of policies) {
    const { kind } = classifyCoiPickerPolicy(policy, today, options);
    if (kind === "in_force") counts.inForce += 1;
    else if (kind === "renewal_offer") counts.renewalOffer += 1;
    else counts.locked += 1;
  }
  return counts;
}

// CSR-2287 — which of the checked terms the service will actually put on the
// certificate. It drops coverage-less shells, EXCEPT when that would empty the
// set, in which case it keeps them (`aggregateCoiPolicies`). Comparing a send
// against the naive "checked with coverage" set instead would report a policy
// mismatch on every shell-only send, which is the one case where nothing went
// wrong.
export function expectedCoiCertificatePolicyIds(
  checkedPolicies: Policy[],
): string[] {
  return selectCoiCertificateCandidates(checkedPolicies).map(
    (policy) => policy.id,
  );
}

// Reads the send route's 202 body as ONE ENTRY PER RECIPIENT ISSUANCE, never a
// union: a recipient whose certificate is missing a policy must not be able to
// hide behind another recipient who got it. A `null` entry is an issuance whose
// snapshot is unreadable (older service, degraded write) and a `null` result is
// a body with no issuances at all — both read as unknown, not as a mismatch, or
// every such send would warn. CSR-2287.
export function coiIssuedPolicyIdsByIssuance(
  responseBody: unknown,
): (string[] | null)[] | null {
  const issuances = (responseBody as { issuances?: unknown } | null)?.issuances;
  if (!Array.isArray(issuances)) return null;
  return issuances.map((issuance) => {
    const coiPolicies = (
      issuance as { coverageLimits?: { coiPolicies?: unknown } } | null
    )?.coverageLimits?.coiPolicies;
    if (!Array.isArray(coiPolicies)) return null;
    const ids = new Set<string>();
    for (const entry of coiPolicies) {
      const policyId = (entry as { policyId?: unknown } | null)?.policyId;
      if (typeof policyId === "string" && policyId) ids.add(policyId);
    }
    return Array.from(ids);
  });
}

// One certificate against what was checked.
export function isCoiPolicySetMismatch(
  expectedIds: string[],
  issuedIds: string[] | null,
): boolean {
  if (issuedIds === null) return false;
  const expected = new Set(expectedIds);
  const issued = new Set(issuedIds);
  if (expected.size !== issued.size) return true;
  return Array.from(expected).some((id) => !issued.has(id));
}

// The whole send: one differing readable issuance is a mismatch, because the
// producer is told the certificate went out as selected and only one recipient
// has to be wrong for that to be false. CSR-2287.
export function hasCoiPolicySetMismatch(
  expectedIds: string[],
  issuedIdsByIssuance: (string[] | null)[] | null,
): boolean {
  if (issuedIdsByIssuance === null) return false;
  return issuedIdsByIssuance.some(
    (issuedIds) =>
      issuedIds !== null && isCoiPolicySetMismatch(expectedIds, issuedIds),
  );
}

export function toggleCoiPolicySelection(
  selectedIds: string[],
  policyId: string,
  checked: boolean,
): string[] {
  const has = selectedIds.includes(policyId);
  if (checked) return has ? selectedIds : [...selectedIds, policyId];
  return has ? selectedIds.filter((id) => id !== policyId) : selectedIds;
}
